import { useState, type ReactNode } from 'react';
import { ActionDialog, TextInput } from './ActionDialog';
import type { ActionState, PartnerAccount } from './pb-types';
import { companiesWord } from './pb-words';

interface Base extends ActionState {
  account: PartnerAccount;
  onClose: () => void;
}

/** Suspend the paying account by hand for an unpaid statement: every company it pays for to Free until a person resumes it. */
export function SuspendAccountDialog(
  p: Base & {
    unpaid: string[];
    onConfirm: (ask: { reason: string; statement: string | null }) => void;
  },
): ReactNode {
  // The statements read can land after the dialog opened (it opens once the account is read): until staff
  // type, the field follows the oldest unpaid statement rather than keeping the empty list's blank.
  const [typed, setTyped] = useState<string | null>(null);
  const statement = typed ?? p.unpaid[0] ?? '';
  const n = companiesWord(p.account.companies);
  return (
    <ActionDialog
      {...p}
      danger
      title={`Suspend ${p.account.name}`}
      confirmLabel={`Suspend and move ${n} to Free`}
      onConfirm={(reason) => p.onConfirm({ reason, statement: statement.trim() || null })}
      consequence={<SuspendConsequence account={p.account} />}
    >
      <TextInput
        label="Unpaid statement"
        hint={
          p.unpaid.length
            ? `Unpaid now: ${p.unpaid.join(', ')}`
            : 'Its number, kept with the reason'
        }
        value={statement}
        onChange={setTyped}
        max={40}
      />
    </ActionDialog>
  );
}

function SuspendConsequence({ account }: { account: PartnerAccount }): ReactNode {
  return (
    <>
      <p>
        All <strong>{companiesWord(account.companies)}</strong> it pays for go to Free now and their
        admins are told to contact {account.name}. Take-ons, plan changes and packs are refused
        until a person resumes it.
      </p>
      <p>
        Renewals keep accruing on its statement: a suspension pauses service, it never skips a bill.
      </p>
    </>
  );
}

export function ResumeAccountDialog(p: Base & { onConfirm: (reason: string) => void }): ReactNode {
  return (
    <ActionDialog
      {...p}
      title={`Resume ${p.account.name}`}
      confirmLabel="Resume account"
      consequence={
        <p>
          Each of its {companiesWord(p.account.companies)} goes back to its plan now, with its full
          pages, except one its partner or staff paused on its own: that stays paused until it is
          resumed.
        </p>
      }
    />
  );
}
